import React, { useEffect, useState } from "react";
import PageHeader from "../../../../../components/PageHeader/PageHeader";
import Loader from "../../../../../components/Loader/Loader";
import { useIntl, defineMessages } from "react-intl";
import ErrorState from "./Components/ErrorState";
import { pageTitles } from "../../../../../utils/messages";
import { BaseRouteWithHeaderWrapper } from "../../../../../components/RouteWithHeaderWrapper/RouteWithHeaderWrapper";
import { useWorkshops } from "../../useWorkshops";
import { Pagetype } from "../../../../../interfaces/Config";
import { useUrl } from "../../../../../hooks/usePage";
import { useActiveSubject } from "../../../../../hooks/useActiveSubject";
import { Filter } from "../../../../../design-system-components/ResourceFilter/Components/filterTypes";
import { useSafeMappingTree } from "../../../../../hooks/useMappingTree";
import { filterLearningSets } from "../../../../SoloAIModuleList/utils";
import { useResourceFilterFlag } from "../../../../../hooks/useResourceFilterFlag";
import ResourceMappingFiltersControlBar from "../../../../../design-system-components/ResourceFilter/ResourceMappingFiltersControlBar";
import { useOverlayTriggerState } from "react-stately";
import { Workshop } from "../../types";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import WorkshopsList from "../../WorkshopsList/WorkshopsList";
import WorkshopDetails from "./Components/WorkshopDetails/WorkshopDetails";
import SimpleBar from "simplebar-react";
import {
    Workmode,
    WorkmodeGrid,
    WorkmodePage,
} from "../../../../../design-system-components/WorkmodeLayout";
import WorkmodeBottomSheet from "../../../../../design-system-components/WorkmodeLayout/WorkmodeBottomSheet/WorkmodeBottomSheet";

import "./TeacherWorkshopsList.scss";
import { ROUTES } from "../../../../../design-system-components/WorkmodeLayout/const";
import EmptyContainer from "../../../../../design-system-components/WorkmodeLayout/EmptyContainer/EmptyContainer";
import { useImages } from "../../../../../hooks/useConfig";
import { defaultImages } from "../../../../../utils/defaultImages";

export const TeacherWorkshopsList = () => {
    const { isTabletOrSmaller } = useBreakpoints();
    const activeSubject = useActiveSubject();
    const resourceFilterPanelFlag = useResourceFilterFlag("workshopsList");
    const { queries, data } = useWorkshops();

    const [workshopInPreview, setWorkshopInPreview] = useState<Workshop>();
    const workshopPreviewModalState = useOverlayTriggerState({});

    const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
    const mappingTree = useSafeMappingTree();
    const filteredWorkshops = mappingTree
        ? filterLearningSets(data, appliedFilters, mappingTree)
        : data;
    const isLoading =
        filteredWorkshops.length === 0 &&
        queries.some(({ isLoading }) => isLoading);
    useEffect(() => {
        // Reset when active subject changes
        setAppliedFilters([]);
    }, [activeSubject]);
    const intl = useIntl();
    const title = intl.formatMessage(pageTitles.teacherWorkshopsList);
    const workModeSelectionUrl = useUrl(Pagetype.WORK_MODES);
    const images = useImages();
    return (
        <BaseRouteWithHeaderWrapper title={title}>
            <WorkmodePage className="workshops-wm">
                <PageHeader
                    title={{ content: title }}
                    backTo={workModeSelectionUrl}
                    className="workshops-wm__header"
                >
                    <div className="workshops-wm__filters">
                        {queries.length === 0 ? (
                            <EmptyContainer
                                img={
                                    images?.resources?.teacher?.workshops
                                        ?.emptyState
                                }
                            >
                                <p>
                                    {intl.formatMessage(messages.noWorkshops)}
                                </p>
                            </EmptyContainer>
                        ) : (
                            <>
                                {!isLoading && resourceFilterPanelFlag && (
                                    <ResourceMappingFiltersControlBar
                                        filtersModalTitle={intl.formatMessage(
                                            pageTitles.teacherWorkshopsList
                                        )}
                                        appliedFilters={appliedFilters}
                                        onAppliedFilterChange={
                                            setAppliedFilters
                                        }
                                        resources={data}
                                    />
                                )}
                                {queries.every((query) => query.isError) && (
                                    <ErrorState />
                                )}
                            </>
                        )}
                    </div>
                </PageHeader>

                <WorkmodeGrid className="workshops-wm__main">
                    <WorkmodeGrid.LeftColumn>
                        <div className="workshops-wm__main__container">
                            <div className="workshops-wm__main__container__workshop-list">
                                <SimpleBar
                                    autoHide={false}
                                    style={{ height: "100%" }}
                                >
                                    <Loader isLoading={isLoading} />
                                    {!isLoading && (
                                        <WorkshopsList
                                            onWorkshopSelection={(workshop) => {
                                                workshopPreviewModalState.open();
                                                if (
                                                    !isTabletOrSmaller &&
                                                    workshopInPreview?.id ===
                                                        workshop.id
                                                ) {
                                                    setWorkshopInPreview(
                                                        undefined
                                                    );
                                                } else
                                                    setWorkshopInPreview(
                                                        workshop
                                                    );
                                            }}
                                            selectedWorkshop={workshopInPreview}
                                            workshopList={filteredWorkshops.map(
                                                (workshop) => workshop.id
                                            )}
                                        />
                                    )}
                                </SimpleBar>
                            </div>
                        </div>
                    </WorkmodeGrid.LeftColumn>
                    <WorkmodeGrid.RightColumn className="workshops-wm__side-container">
                        {workshopInPreview ? (
                            <RightColumnContent
                                workshopInPreview={workshopInPreview}
                            />
                        ) : (
                            <div className="workshops-wm__no-selection">
                                <EmptyContainer
                                    img={
                                        images?.resources?.teacher?.workshops
                                            ?.rightColumn
                                    }
                                    defaultImg={
                                        defaultImages.teacherResourcesWorkshopRightColumn
                                    }
                                >
                                    <p>
                                        {intl.formatMessage(
                                            messages.workshopSidePlaceholder
                                        )}
                                    </p>
                                </EmptyContainer>
                            </div>
                        )}
                    </WorkmodeGrid.RightColumn>
                </WorkmodeGrid>

                {isTabletOrSmaller && workshopInPreview && (
                    <WorkmodeBottomSheet
                        ariaLabel={intl.formatMessage(messages.workshopDetails)}
                        state={workshopPreviewModalState}
                    >
                        <RightColumnContent
                            workshopInPreview={workshopInPreview}
                        />
                    </WorkmodeBottomSheet>
                )}
            </WorkmodePage>
        </BaseRouteWithHeaderWrapper>
    );
};

export default TeacherWorkshopsList;

interface RightColumnContentProps {
    workshopInPreview: Workshop;
}

const RightColumnContent = ({ workshopInPreview }: RightColumnContentProps) => {
    const intl = useIntl();
    const urlMode = useUrl(Pagetype.WORKMODE_WORKSHOP);
    return (
        <>
            <Workmode.ResourcePreview
                className="workshops-wm__side-container__resource-preview"
                title={workshopInPreview.name}
                description={workshopInPreview.description}
                previewUrl={`${urlMode}/${ROUTES.seeWorkshop}/${workshopInPreview.id}`}
            />
            <Workmode.AddAssignment
                className="workshops-wm__side-container__add-assignement"
                title={intl.formatMessage(messages.padsTitle)}
                addButton={{
                    label: intl.formatMessage(messages.padsCreate),
                    icons: [
                        {
                            path: "add",
                        },
                    ],
                }}
                assignmentUrl={`${urlMode}/${ROUTES.createPad}/${workshopInPreview.id}`}
            />
            <WorkshopDetails workshopInPreview={workshopInPreview} />
        </>
    );
};

export const messages = defineMessages({
    noPadCreated: {
        id: "workshop-no-pad-created",
        defaultMessage: "You have not created any pad yet.",
    },
    padsCreate: {
        id: "workshop-pads-create",
        defaultMessage: "Create a pad",
    },
    padsTitle: {
        id: "workshop-pads-title",
        defaultMessage: "Collaborative pads",
    },
    workshopDetails: {
        id: "workshop-details",
        defaultMessage: "Workshop details",
    },
    workshopSidePlaceholder: {
        id: "workshop-side-placeholder",
        defaultMessage:
            "Select a workshop to get an overview of its recipients and their progress.",
    },
    noWorkshops: {
        id: "workshop-noWorkshop",
        defaultMessage: "There are no workshops available at this time.",
    },
});
